import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { useTran } from "@/hooks";
import { Button } from "@/components/ui/button";
import { showToast } from "@/lib/utils/toast";
import { surveysService } from "../services";
import { useSurveyTakeStore } from "../hooks";
import SurveyTakeHeader from "./SurveyTakeHeader";
import SurveyQuestionField from "./SurveyQuestionField";
import SurveyTakeResult from "./SurveyTakeResult";
import {
  calculateMaxScore,
  chunkSurveyQuestions,
  countAnsweredQuestions,
  resolveSurveyType,
} from "../utils/survey-take";

const SurveyTake = ({
  survey,
  shareToken,
  externalRespondent,
  disableSubmit = false,
  showHeader = true,
  stickyFooter = false,
  onSubmitted,
}) => {
  const { t } = useTran();
  const takeStore = useSurveyTakeStore();

  useEffect(() => {
    if (survey !== undefined) {
      takeStore.setSurvey(survey);
    }
  }, [survey?._id, survey, takeStore]);

  const activeSurvey = survey ?? takeStore.survey;
  const responses = takeStore.responses;
  const submitting = takeStore.submitting;
  const currentStep = takeStore.currentStep;
  const score = takeStore.score;
  const startTime = takeStore.startTime;
  const viewMode = takeStore.viewMode;
  const submissionMeta = takeStore.submissionMeta;

  const isGuestMode = Boolean(externalRespondent);
  const guestInfo = externalRespondent || {};
  const guestCompanyName = guestInfo.organization || "";

  const surveyType = useMemo(
    () => resolveSurveyType(activeSurvey),
    [activeSurvey],
  );
  const steps = useMemo(
    () => chunkSurveyQuestions(activeSurvey?.questions || []),
    [activeSurvey?.questions],
  );
  const currentQuestions = steps[currentStep]?.questions || [];
  const hasSteps = steps.length > 0;
  const currentCategory = steps[currentStep]?.category || "common";

  const totalQuestions = activeSurvey?.questions?.length || 0;
  const answeredQuestions = countAnsweredQuestions(responses);
  const progress =
    totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
  const progressPercent = Math.round(progress);

  const maxPossibleScore = useMemo(
    () => calculateMaxScore(activeSurvey?.questions || []),
    [activeSurvey?.questions],
  );

  const handleChange = (questionId, value) => {
    takeStore.setResponse(questionId, value);
  };

  const handleSubmit = async () => {
    if (!activeSurvey?._id) return;
    const missingRequired = (activeSurvey.questions || []).find((question) => {
      if (!question.required) return false;
      const key = question._id?.toString();
      const value = responses[key];
      if (Array.isArray(value)) return value.length === 0;
      return value === undefined || value === null || value === "";
    });
    if (missingRequired) {
      showToast.error(t("Please answer all required questions."));
      return;
    }

    takeStore.setSubmitting(true);
    try {
      const completionTimeInSeconds = Math.floor(
        (Date.now() - startTime) / 1000,
      );
      const payload = {
        surveyId: activeSurvey._id,
        surveyType,
        completionTimeInSeconds,
        answers: (activeSurvey.questions || [])
          .map((question) => {
            const key = question._id?.toString();
            const answer = responses[key];
            return {
              questionId: question._id,
              questionType: question.questionType,
              answer: answer ?? (Array.isArray(answer) ? [] : ""),
            };
          })
          .filter((answer) => {
            if (Array.isArray(answer.answer)) return answer.answer.length > 0;
            return (
              answer.answer !== "" &&
              answer.answer !== null &&
              answer.answer !== undefined
            );
          }),
        shareToken: shareToken || undefined,
      };

      if (isGuestMode) {
        payload.externalRespondent = {
          name: guestInfo.name?.trim(),
          email: guestInfo.email?.trim().toLowerCase(),
          organization: guestCompanyName || undefined,
        };
        payload.respondentCompanyId = guestInfo.companyId || undefined;
      }

      const submitPromise = surveysService.submitSurveyResponse(payload);
      const data = await submitPromise;
      if (!data) {
        throw new Error(t("Unable to submit response"));
      }
      const payloadData = data?.data || data;
      const totalPoints = payloadData?.response?.totalScore;
      showToast.success(t("Survey response submitted successfully!"));

      if (surveyType === "StandardSurvey" || surveyType === "CustomSurvey") {
        const maxScore = maxPossibleScore || totalQuestions || null;
        const scorePct =
          maxScore && totalPoints != null && maxScore > 0
            ? Math.round((totalPoints / maxScore) * 100)
            : null;
        takeStore.setScore(totalPoints ?? null);
        takeStore.setSubmissionMeta({
          totalQuestions,
          answered: answeredQuestions,
          maxPossibleScore: maxScore,
          scorePercentage: scorePct,
          completionTimeInSeconds,
          submittedAt:
            payloadData?.response?.submittedAt || new Date().toISOString(),
        });
        takeStore.setViewMode("result");
      } else {
        takeStore.resetSession();
        onSubmitted?.();
      }
    } catch (error) {
      showToast.error(error?.message || t("Unable to submit response"));
    } finally {
      takeStore.setSubmitting(false);
    }
  };

  const handleResultClose = () => {
    if (isGuestMode) {
      window.location.assign("/");
      return;
    }
    takeStore.resetSession();
    onSubmitted?.();
  };

  const handleResultRestart = () => {
    takeStore.resetSession();
  };

  if (!activeSurvey) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">
          {t("Select a survey to take.")}
        </p>
      </div>
    );
  }

  if (!hasSteps) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-6">
        <p className="text-sm text-muted-foreground">
          {t("This survey has no questions yet.")}
        </p>
      </div>
    );
  }
  const validateCurrentStep = () => {
    const stepsQuestions = currentQuestions || [];
    const missingRequired = stepsQuestions.find((question) => {
      if (!question.required) return false;
      const key = question._id?.toString();
      const value = responses[key];
      if (Array.isArray(value)) return value.length === 0;
      return value === undefined || value === null || value === "";
    });
    return !missingRequired;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) {
      showToast.warning(
        t("Please answer all required questions in this step."),
      );
      return;
    }
    takeStore.setCurrentStep(Math.min(steps.length - 1, currentStep + 1));
  };

  if (viewMode === "result") {
    const answered = submissionMeta?.answered ?? answeredQuestions;
    const total = submissionMeta?.totalQuestions ?? totalQuestions;
    const completionTimeInSeconds = submissionMeta?.completionTimeInSeconds;
    const submittedAt = submissionMeta?.submittedAt || null;
    const resultMaxScore =
      submissionMeta?.maxPossibleScore ?? maxPossibleScore ?? 0;
    const finalScore = score ?? null;
    return (
      <SurveyTakeResult
        surveyTitle={activeSurvey.title}
        score={finalScore}
        maxScore={resultMaxScore}
        answered={answered}
        total={total}
        durationSeconds={completionTimeInSeconds}
        submittedAt={submittedAt}
        isGuestMode={isGuestMode}
        onClose={handleResultClose}
        onRestart={handleResultRestart}
      />
    );
  }

  const footerActions = (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Button
        variant="outline"
        className="flex items-center gap-2 bg-transparent"
        onClick={() => takeStore.setCurrentStep(Math.max(0, currentStep - 1))}
        disabled={currentStep === 0}
      >
        <ChevronLeft className="h-4 w-4" />
        {t("Previous")}
      </Button>
      <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
        <span>
          {t("Answered {{answered}}/{{total}}", {
            answered: answeredQuestions,
            total: totalQuestions,
          })}
        </span>

        <span>•</span>
        <span>
          {t("{{percent}}% complete", { percent: Math.round(progress) })}
        </span>
      </div>
      {currentStep < steps.length - 1 ? (
        <Button className="flex items-center gap-2" onClick={handleNext}>
          {t("Next")}
          <ChevronRight className="h-4 w-4" />
        </Button>
      ) : (
        <Button
          onClick={handleSubmit}
          disabled={submitting || disableSubmit}
          className="flex items-center gap-2"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("Submitting...")}
            </>
          ) : (
            t("Submit Survey")
          )}
        </Button>
      )}
    </div>
  );

  const footerContent = stickyFooter ? (
    <div className="sticky bottom-0 z-10 -mx-6 mt-4 border-t border-border bg-background/95 px-6 py-3 backdrop-blur">
      {footerActions}
    </div>
  ) : (
    footerActions
  );

  return (
    <div className="w-full space-y-6 overflow-hidden">
      {showHeader && viewMode !== "result" ? (
        <div className="rounded-radius border border-border bg-card/80 p-4 shadow-sm">
          <SurveyTakeHeader
            title={activeSurvey?.title}
            description={activeSurvey?.description}
            currentStep={currentStep + 1}
            totalSteps={steps.length || 1}
            category={currentCategory}
            stepQuestions={currentQuestions.length}
            answeredQuestions={answeredQuestions}
            totalQuestions={totalQuestions}
            progress={progress}
            progressPercent={progressPercent}
          />
        </div>
      ) : null}

      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ duration: 0.2 }}
          className="space-y-4"
        >
          {currentQuestions.map((question, index) => {
            const categoryLabel = (question.category || "common").toUpperCase();
            const responseKey = question._id?.toString() || String(index);
            const rawValue = responses[responseKey];
            const resolvedValue = Array.isArray(rawValue)
              ? rawValue
              : question.questionType === "multiple_choice"
                ? []
                : (rawValue ?? "");
            return (
              <div
                key={question._id || index}
                className="rounded-radius border border-border bg-card p-5 shadow-sm"
              >
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
                  <span className="text-primary">{categoryLabel}</span>
                  {question.required ? (
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
                      {t("Required")}
                    </span>
                  ) : null}
                </div>
                <div className="mt-2 flex items-start justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      {question.questionText}
                    </p>
                    {question.description ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {question.description}
                      </p>
                    ) : null}
                  </div>
                </div>
                <div className="mt-3">
                  <SurveyQuestionField
                    question={question}
                    value={resolvedValue}
                    onChange={(value) => handleChange(responseKey, value)}
                  />
                </div>
              </div>
            );
          })}
        </motion.div>
      </AnimatePresence>

      {footerContent}
    </div>
  );
};

export default observer(SurveyTake);
